import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCollections,
  deleteCollection,
  duplicateCollection,
  createDefaultCollections,
  selectCollections,
  selectCollectionsStatus
} from './collectionsSlice';
import CollectionModal from './CollectionModal';
import { useToast } from '../../components/ui/Toast';
import { getStoreUrl } from '../../config/platformConfig';
import './Collections.css';

const Collections = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const collections = useSelector(selectCollections);
  const status = useSelector(selectCollectionsStatus);
  const store = useSelector(state => state.store?.current);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  useEffect(() => {
    dispatch(fetchCollections({ includeProducts: true }));
  }, [dispatch]);

  const handleCreateCollection = () => {
    setEditingCollection(null);
    setIsModalOpen(true);
  };

  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
    setIsModalOpen(true);
  };

  const handleDeleteCollection = async (collection) => {
    if (!window.confirm(`Are you sure you want to delete "${collection.name}"?`)) {
      return;
    }

    try {
      await dispatch(deleteCollection(collection.id)).unwrap();
      showToast('Collection deleted successfully', 'success');
    } catch (error) {
      showToast(error || 'Failed to delete collection', 'error');
    }
  };

  const handleDuplicateCollection = async (collection) => {
    try {
      await dispatch(duplicateCollection(collection.id)).unwrap();
      showToast('Collection duplicated successfully', 'success');
    } catch (error) {
      showToast(error || 'Failed to duplicate collection', 'error');
    }
  };

  const handleCreateDefaults = async () => {
    try {
      const result = await dispatch(createDefaultCollections()).unwrap();
      showToast(`Created ${result.length} default collections`, 'success');
    } catch (error) {
      showToast(error || 'Failed to create default collections', 'error');
    }
  };

  const handleCopyShareLink = async (collection) => {
    // Build storefront URL using the platform's URL builder
    // This handles subdomain, custom domain, and localhost correctly
    const storeSlug = store?.slug;
    if (!storeSlug) {
      showToast('Store not found', 'error');
      return;
    }

    // Check if store has a custom domain
    const customDomain = store?.customDomains?.find(d => d.status === 'ACTIVE')?.domain;
    let storefrontUrl;

    if (customDomain) {
      // Use custom domain if available
      storefrontUrl = `https://${customDomain}/collection/${collection.slug}`;
    } else {
      // Use platform subdomain via getStoreUrl helper
      const baseStoreUrl = getStoreUrl(storeSlug);
      storefrontUrl = `${baseStoreUrl}/collection/${collection.slug}`;
    }

    try {
      await navigator.clipboard.writeText(storefrontUrl);
      setCopiedId(collection.id);
      showToast('Share link copied to clipboard', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      showToast('Failed to copy link', 'error');
    }
  };

  const filteredCollections = (collections || []).filter(collection => {
    const matchesSearch = collection.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' ||
      (filterType === 'manual' && collection.collectionType === 'MANUAL') ||
      (filterType === 'automatic' && collection.collectionType === 'AUTOMATIC');
    return matchesSearch && matchesType;
  });

  const getProductCount = (collection) => {
    if (collection._count?.products !== undefined) {
      return collection._count.products;
    }
    return collection.products?.length || 0;
  };

  // Get collection image with product images as fallback
  const getCollectionImage = (collection) => {
    // First try the collection's own image
    if (collection.coverImage || collection.image) {
      return collection.coverImage || collection.image;
    }
    // Fall back to first product image if available
    if (collection.products && collection.products.length > 0) {
      for (const cp of collection.products) {
        const product = cp.product || cp;
        if (product?.primaryImage) return product.primaryImage;
        if (product?.gallery?.[0]) return product.gallery[0];
      }
    }
    return null;
  };

  if (status === 'loading' && (!collections || collections.length === 0)) {
    return (
      <div className="collections-page">
        <div className="collections-loading">
          <div className="loading-spinner"></div>
          <p>Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="collections-page">
      <div className="collections-header">
        <div className="collections-header-left">
          <h1>Collections</h1>
          <p className="collections-subtitle">
            Organize your products into collections for easier browsing
          </p>
        </div>
        <div className="collections-header-actions">
          {(!collections || collections.length === 0) && (
            <button
              className="collections-btn collections-btn-secondary"
              onClick={handleCreateDefaults}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Defaults
            </button>
          )}
          <button
            className="collections-btn collections-btn-primary"
            onClick={handleCreateCollection}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Collection
          </button>
        </div>
      </div>

      <div className="collections-filters">
        <div className="collections-search">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="collections-filter-group">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="collections-filter-select"
          >
            <option value="all">All Types</option>
            <option value="manual">Manual</option>
            <option value="automatic">Automatic</option>
          </select>
        </div>
        <div className="collections-view-toggle">
          <button
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
          <button
            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {filteredCollections.length === 0 ? (
        <div className="collections-empty">
          <div className="collections-empty-icon">
            <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3>No collections yet</h3>
          <p>Create collections to organize your products and make them easier to find.</p>
          <div className="collections-empty-actions">
            <button
              className="collections-btn collections-btn-primary"
              onClick={handleCreateCollection}
            >
              Create Collection
            </button>
            <button
              className="collections-btn collections-btn-secondary"
              onClick={handleCreateDefaults}
            >
              Use Default Templates
            </button>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="collections-grid">
          {filteredCollections.map((collection) => {
            const collectionImage = getCollectionImage(collection);
            return (
              <div key={collection.id} className="collection-card">
                <div className="collection-card-image">
                  {collectionImage ? (
                    <img src={collectionImage} alt={collection.name} />
                  ) : (
                    <div className="collection-card-placeholder">
                      <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                  <div className="collection-card-badges">
                    <span className={`collection-type-badge ${collection.collectionType.toLowerCase()}`}>
                      {collection.collectionType === 'AUTOMATIC' ? 'Auto' : 'Manual'}
                    </span>
                    {collection.featured && (
                      <span className="collection-featured-badge">Featured</span>
                    )}
                    {!collection.isPublic && (
                      <span className="collection-private-badge">Private</span>
                    )}
                  </div>
                </div>

                <div className="collection-card-content">
                  <h3 className="collection-card-title">{collection.name}</h3>
                  {collection.description && (
                    <p className="collection-card-description">{collection.description}</p>
                  )}
                  <div className="collection-card-meta">
                    <span className="collection-product-count">
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      {getProductCount(collection)} products
                    </span>
                  </div>
                </div>

                <div className="collection-card-actions">
                  <button
                    className="collection-action-btn"
                    onClick={() => handleEditCollection(collection)}
                    title="Edit"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className="collection-action-btn"
                    onClick={() => handleCopyShareLink(collection)}
                    title="Copy share link"
                  >
                    {copiedId === collection.id ? (
                      <svg width="18" height="18" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                    )}
                  </button>
                  <button
                    className="collection-action-btn"
                    onClick={() => handleDuplicateCollection(collection)}
                    title="Duplicate"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  </button>
                  <button
                    className="collection-action-btn collection-action-delete"
                    onClick={() => handleDeleteCollection(collection)}
                    title="Delete"
                  >
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="collections-list">
          <div className="collections-list-header">
            <div className="list-col list-col-image">Image</div>
            <div className="list-col list-col-name">Collection</div>
            <div className="list-col list-col-type">Type</div>
            <div className="list-col list-col-products">Products</div>
            <div className="list-col list-col-status">Status</div>
            <div className="list-col list-col-actions">Actions</div>
          </div>
          {filteredCollections.map((collection) => {
            const collectionImage = getCollectionImage(collection);
            return (
              <div key={collection.id} className="collections-list-row">
                <div className="list-col list-col-image">
                  <div className="list-image-wrapper">
                    {collectionImage ? (
                      <img src={collectionImage} alt={collection.name} />
                    ) : (
                      <div className="list-image-placeholder">
                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                          <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
                <div className="list-col list-col-name">
                  <div className="list-name-content">
                    <span className="list-collection-name">{collection.name}</span>
                    {collection.description && (
                      <span className="list-collection-desc">{collection.description}</span>
                    )}
                  </div>
                </div>
                <div className="list-col list-col-type">
                  <span className={`collection-type-badge ${collection.collectionType.toLowerCase()}`}>
                    {collection.collectionType === 'AUTOMATIC' ? 'Auto' : 'Manual'}
                  </span>
                </div>
                <div className="list-col list-col-products">
                  {getProductCount(collection)}
                </div>
                <div className="list-col list-col-status">
                  <div className="list-status-badges">
                    {collection.featured && (
                      <span className="collection-featured-badge">Featured</span>
                    )}
                    {collection.isPublic ? (
                      <span className="status-badge status-public">Public</span>
                    ) : (
                      <span className="collection-private-badge">Private</span>
                    )}
                  </div>
                </div>
                <div className="list-col list-col-actions">
                  <button
                    className="collection-action-btn"
                    onClick={() => handleEditCollection(collection)}
                    title="Edit"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    className="collection-action-btn"
                    onClick={() => handleCopyShareLink(collection)}
                    title="Copy share link"
                  >
                    {copiedId === collection.id ? (
                      <svg width="16" height="16" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                    )}
                  </button>
                  <button
                    className="collection-action-btn"
                    onClick={() => handleDuplicateCollection(collection)}
                    title="Duplicate"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  </button>
                  <button
                    className="collection-action-btn collection-action-delete"
                    onClick={() => handleDeleteCollection(collection)}
                    title="Delete"
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isModalOpen && (
        <CollectionModal
          collection={editingCollection}
          onClose={() => {
            setIsModalOpen(false);
            setEditingCollection(null);
          }}
        />
      )}
    </div>
  );
};

export default Collections;
